import React from "react";
import {
  Button,
  Dropdown,
  Iconography,
  IconographySizes,
  IconographyThemes,
  Loader,
  LoaderSizes,
  Modal,
  ModalVariants,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { PartnerDetailType } from "../../../../../../redux/slices/partner-details/Types";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  getAllPartnersSagaAction,
  unlinkPartnerSagaAction,
} from "../../../../../../redux/saga/partner-details/saga-actions";
import {
  linkPartnerSagaAction,
  getPartnerDetailsSagaAction,
} from "../../../../../../redux/saga/partner-details/saga-actions";
import "./LinkPartnerModal.scss";

const LinkPartnerModal: React.FC<{
  partnerDetails: PartnerDetailType;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ partnerDetails, setModalOpen }) => {
  const [reason, setReason] = React.useState<string>("");
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [selectedPartner, setSelectedPartner] = React.useState(null);
  const { allPartners, status } = useAppSelector(
    (state) => state.partner_details
  );
  const { loggedUserDetail } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (allPartners.length === 0) dispatch(getAllPartnersSagaAction());
  }, []);

  const PartnerOptions = allPartners
    .filter(
      (partner) =>
        !(partner.status === "Inactive" || partner.ID == partnerDetails.ID)
    )
    .map((partner) => {
      return { label: partner.partner_name, value: partner.ID };
    });

  React.useEffect(() => {
    if (partnerDetails.parent_partner) {
      const parentPartner = allPartners.find(
        (partner) => partner.ID === partnerDetails.parent_partner.ID
      );
      setSelectedPartner(
        parentPartner
          ? { label: parentPartner.partner_name, value: parentPartner.ID }
          : undefined
      );
      setReason(partnerDetails.metadata?.reason);
    }
  }, [allPartners]);

  const onPartnerSelect = (data: { label: string; value: string }) => {
    setSelectedPartner(data);
  };

  const onSubmitButtonClick = () => {
    if (partnerDetails.parent_partner)
      dispatch(unlinkPartnerSagaAction(partnerDetails.ID));
    else
      dispatch(
        linkPartnerSagaAction({
          parentPartnerID: selectedPartner.value,
          childPartnerID: partnerDetails.ID,
          reason: reason,
          initiatedBy: loggedUserDetail.email,
        })
      );
    setSubmitted(true);
  };

  const footerConfig = submitted
    ? {}
    : {
        secondaryBtnLabel: "Cancel",
      };
  const onModalClose = () => {
    if (submitted && status.linkPartnerStatus) {
      dispatch(getPartnerDetailsSagaAction(partnerDetails.ID));
      dispatch(getAllPartnersSagaAction());
    }
    setModalOpen(false);
  };
  const isLinked = Boolean(partnerDetails.parent_partner);
  const headerText = isLinked ? "Unlink from Parent" : "Link To Parent";
  const buttonLabel = isLinked ? "Unlink From Parent" : "Link To Parent";
  const confirmMessage = isLinked
    ? "Linked to following parent partner"
    : "Find and select parent partner to link";

  return (
    <Modal
      headerText={headerText}
      size={ModalVariants.Medium}
      footerConfig={footerConfig}
      onClose={() => onModalClose()}
      className="link-partner-modal"
    >
      {submitted ? (
        <div className="status-message-modal-container">
          {status.linkPartnerLoading ? (
            <Loader variant={LoaderSizes.large} />
          ) : status.linkPartnerStatus ? (
            <>
              <Iconography
                icon="circleCheck"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Success}
              />
              <div className="message">
                {isLinked ? "Partner Unlinked" : "Partner Linked"}
              </div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => onModalClose()}
              />
            </>
          ) : (
            <>
              <Iconography
                icon="circleXmark"
                size={IconographySizes.XLarge}
                theme={IconographyThemes.Error}
              />
              <div className="message">Something went wrong</div>
              <Button
                variant="primary"
                label="Continue"
                onClick={() => onModalClose()}
              />
            </>
          )}
        </div>
      ) : (
        <div className="message-button-container">
          <p className="confirm-message">{confirmMessage}</p>
          <div className="link-to-parent">
            <div className="partner-image-name">
              <div className="icon-wrapper">
                <Iconography icon="buildings" size={IconographySizes.Medium} />
              </div>
              <div className="name">{partnerDetails.partner_name}</div>
            </div>
            <Iconography
              icon="arrowRightArrowLeft"
              size={IconographySizes.Medium}
            />
            <div className="partner-image-name">
              <div className="icon-wrapper">
                <Iconography icon="buildings" size={IconographySizes.Medium} />
              </div>
              {selectedPartner ? (
                <div className="name">{selectedPartner.label}</div>
              ) : (
                <div className="selected-partner-placeholder" />
              )}
            </div>
          </div>
          <Dropdown
            label="Link to:"
            id="selected-partner"
            name="selected-partner"
            options={PartnerOptions}
            selected={selectedPartner}
            onSelect={onPartnerSelect}
            isSearchable={true}
            isClearable={true}
            isDisabled={isLinked}
          />
          <TextField
            id="reason"
            name="reason"
            label="Reason For Linking"
            value={reason}
            placeholder="Enter here"
            theme="dark"
            onChange={(e) => setReason(e.currentTarget.value)}
            disabled={isLinked}
          />
          <Button
            variant="primary"
            label={buttonLabel}
            onClick={onSubmitButtonClick}
            disabled={!((reason && selectedPartner) || isLinked)}
          />
        </div>
      )}
    </Modal>
  );
};

export default LinkPartnerModal;
